"use client";

import React, { useState } from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { CloudUpload, ArrowRight, ArrowLeft, CircleCheck, Loader, FileText, Building2, User } from "lucide-react";
import { cn } from "@/lib/utils";
const commonInputClasses = "placeholder:text-muted-foreground/50 selection:bg-primary selection:text-primary-foreground dark:bg-card border-border flex w-full min-w-0 rounded-sm border-2 bg-transparent px-3 py-1 text-base shadow-none transition-all outline-none disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-primary focus-visible:ring-0 aria-invalid:border-destructive";
export default function ServiceRequestCenter() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="143433" id="quote-request" ref={ref} className={cn("bg-background dark relative overflow-hidden py-24 transition-all duration-1000", isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0")}>
            {}
            <div className="bg-primary/5 absolute top-0 left-0 h-full w-full bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-primary/10 via-transparent to-transparent opacity-30" />
            <div className="absolute top-20 -left-20 h-72 w-72 rounded-full bg-secondary/10 blur-3xl" />

            <div className="container relative mx-auto max-w-4xl px-4">
                <div className="mb-12 text-center">
                    <h2 className="text-foreground mb-4 text-4xl font-default font-extrabold uppercase tracking-tighter sm:text-5xl md:text-6xl">
                        Quote Request
                    </h2>
                    <p className="text-muted-foreground font-mono mx-auto max-w-2xl text-lg uppercase tracking-tight">
                        Precision last-mile delivery and regional freight solutions for DFW and the Southeast. Secure your dispatch below.
                    </p>
                </div>

                <FormProvider formKey="quote_request_contact" formId="quote_request_contact" sectionName="Service Request Center" defaultValues={{
        name: "",
        email: "",
        phone: "",
        service_type: "",
        request_details: "",
        preferred_date: "",
        manifest_file: ""
      }} validationRules={{
        name: {
          required: true,
          minLength: 2
        },
        email: {
          required: true,
          pattern: "email"
        },
        phone: {
          required: true
        },
        service_type: {
          required: true
        },
        request_details: {
          required: true,
          minLength: 10
        }
      }} submitText="Secure Dispatch" successMessage="Request Transmitted. TransitLink dispatch will verify your route and vehicle availability shortly.">
                    <MultiStepForm />
                </FormProvider>
            </div>
        </section>;
}
function MultiStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [step, setStep] = useState(1);
  const totalSteps = 3;
  const progress = step / totalSteps * 100;
  const nextStep = () => setStep(prev => Math.min(prev + 1, totalSteps));
  const prevStep = () => setStep(prev => Math.max(prev - 1, 1));
  if (isSubmitted && !submitError) {
    return <Card className="border-border border-2 bg-card mx-auto max-w-2xl rounded-sm p-8 text-center shadow-none">
                <div className="mb-6 flex justify-center">
                    <div className="bg-primary rounded-none p-4">
                        <CircleCheck className="text-primary-foreground size-12" />
                    </div>
                </div>
                <CardTitle className="mb-4 font-default text-3xl font-extrabold uppercase tracking-tighter">Transmission Confirmed</CardTitle>
                <CardDescription className="mb-8 font-mono text-base text-foreground/80">
                    {successMessage}
                </CardDescription>
                <Button onClick={() => {
        resetForm();
        setStep(1);
      }} variant="outline" className="border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground min-w-[200px] rounded-none uppercase font-extrabold">
                    New Request
                </Button>
            </Card>;
  }
  return <Card className="border-border border-2 bg-card relative overflow-hidden rounded-sm shadow-none">
            <Progress value={progress} className="bg-muted absolute top-0 left-0 h-2 w-full rounded-none [&>div]:bg-primary" />
            
            <CardHeader className="pb-4 pt-10">
                <div className="mb-8 flex items-center justify-between px-2">
                    {[1, 2, 3].map(i => <div key={i} data-index={i} className="flex flex-col items-center gap-2">
                            <div className={cn("flex size-12 items-center justify-center rounded-none border-2 text-sm font-extrabold transition-colors", step >= i ? "border-primary bg-primary text-primary-foreground" : "border-secondary bg-muted text-muted-foreground")}>
                                {i === 1 && <User className="size-5" />}
                                {i === 2 && <Building2 className="size-5" />}
                                {i === 3 && <FileText className="size-5" />}
                            </div>
                            <span className={cn("hidden text-xs font-extrabold uppercase tracking-widest sm:block", step >= i ? "text-primary" : "text-muted-foreground")}>
                                {i === 1 ? "Contact" : i === 2 ? "Logistics" : "Vehicle"}
                            </span>
                        </div>)}
                    <div className="bg-secondary absolute top-16 left-[15%] -z-10 h-[2px] w-[70%] sm:top-16" />
                </div>
                <CardTitle className="font-default text-2xl font-extrabold uppercase tracking-tighter text-foreground">
                    {step === 1 && "Contact Information"}
                    {step === 2 && "Logistics & Material"}
                    {step === 3 && "Fleet Requirements"}
                </CardTitle>
                <CardDescription className="font-mono text-sm uppercase text-muted-foreground">
                    {step === 1 && "Identify the primary point of contact for this dispatch."}
                    {step === 2 && "Specify cargo type and route coordinates."}
                    {step === 3 && "Finalize documentation and vehicle specifications."}
                </CardDescription>
            </CardHeader>

            <CardContent>
                <Form onSubmit={handleSubmit}>
                    {}
                    <div className={cn("space-y-6", step !== 1 && "hidden")}>
                        <div className="grid gap-6 md:grid-cols-2">
                            <FormField control={control} name="name" render={({
              field
            }) => <FormItem>
                                        <FormLabel className="font-default font-bold uppercase tracking-wider">Contact Name <span className="text-primary">*</span></FormLabel>
                                        <FormControl>
                                            <Input className={commonInputClasses} placeholder="Operations Manager Name" {...field} />
                                        </FormControl>
                                        <FormDescription className="font-mono text-[10px] uppercase">Full legal name for manifest.</FormDescription>
                                        <FormMessage />
                                    </FormItem>} />
                            <FormField control={control} name="email" render={({
              field
            }) => <FormItem>
                                        <FormLabel className="font-default font-bold uppercase tracking-wider">Corporate Email <span className="text-primary">*</span></FormLabel>
                                        <FormControl>
                                            <Input className={commonInputClasses} type="email" placeholder="logistics@company.com" {...field} />
                                        </FormControl>
                                        <FormDescription className="font-mono text-[10px] uppercase">Primary dispatch communication channel.</FormDescription>
                                        <FormMessage />
                                    </FormItem>} />
                        </div>
                        <FormField control={control} name="phone" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="font-default font-bold uppercase tracking-wider">Direct Phone <span className="text-primary">*</span></FormLabel>
                                    <FormControl>
                                        <Input className={commonInputClasses} type="tel" placeholder="+1-469-590-4364" {...field} />
                                    </FormControl>
                                    <FormDescription className="font-mono text-[10px] uppercase">For driver/dispatch coordination.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    {}
                    <div className={cn("space-y-6", step !== 2 && "hidden")}>
                        <FormField control={control} name="service_type" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="font-default font-bold uppercase tracking-wider">Material Category <span className="text-primary">*</span></FormLabel>
                                    <FormControl>
                                        <select className={cn(commonInputClasses, "h-11 py-1.5 font-mono uppercase")} {...field}>
                                            <option value="" disabled>Select Material Type</option>
                                            {["Stone & Masonry", "Lumber & Timber", "Bulk Bulk Materials", "Heavy Industrial", "Palletized Freight"].map((option, i) => <option key={option} data-index={i} value={option} className="bg-card">
                                                    {option}
                                                </option>)}
                                        </select>
                                    </FormControl>
                                    <FormDescription className="font-mono text-[10px] uppercase">Select the primary material for transport.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />

                        <FormField control={control} name="request_details" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="font-default font-bold uppercase tracking-wider">Logistics Details <span className="text-primary">*</span></FormLabel>
                                    <FormControl>
                                        <textarea className={cn(commonInputClasses, "min-h-[120px] py-2 resize-y font-mono")} placeholder="Pickup Address, Dropoff Point, and specific site access instructions..." {...field} />
                                    </FormControl>
                                    <FormDescription className="font-mono text-[10px] uppercase">Include loading dock info or site constraints.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />

                        <FormField control={control} name="preferred_date" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="font-default font-bold uppercase tracking-wider">Requested Dispatch Date</FormLabel>
                                    <FormControl>
                                        <Input className={cn(commonInputClasses, "h-11 font-mono uppercase")} type="date" {...field} />
                                    </FormControl>
                                    <FormDescription className="font-mono text-[10px] uppercase">Preferred window for pickup.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />
                    </div>

                    {}
                    <div className={cn("space-y-6", step !== 3 && "hidden")}>
                        <FormField control={control} name="manifest_file" render={({
            field
          }) => <FormItem>
                                    <FormLabel className="font-default font-bold uppercase tracking-wider">Supporting Documentation</FormLabel>
                                    <FormControl>
                                        <div className="border-secondary hover:border-primary/50 bg-secondary/10 relative flex flex-col items-center justify-center rounded-none border-2 border-dashed p-12 text-center transition-colors">
                                            <Input type="file" className="absolute inset-0 h-full w-full cursor-pointer opacity-0" {...field} value={undefined} />
                                            <div className="bg-card mb-4 border-2 border-border p-4 shadow-none">
                                                <CloudUpload className="text-primary size-10" />
                                            </div>
                                            <h4 className="text-foreground font-default mb-1 font-extrabold uppercase tracking-tight">Upload Manifest / Site Plan</h4>
                                            <p className="text-muted-foreground font-mono text-xs uppercase">
                                                Drag files or click to browse
                                            </p>
                                            <p className="text-muted-foreground/50 mt-4 font-mono text-[10px] uppercase">
                                                PDF, DOCX, JPG (Max 10MB)
                                            </p>
                                        </div>
                                    </FormControl>
                                    <FormDescription className="font-mono text-[10px] uppercase">Upload logistics manifests, resumes for drivers, or site diagrams.</FormDescription>
                                    <FormMessage />
                                </FormItem>} />

                        {submitError && <div className="bg-destructive/10 border-destructive/20 rounded-none border-2 p-4 font-mono text-sm text-destructive uppercase">
                                {submitError}
                            </div>}
                    </div>

                    {}
                    <div className="mt-10 flex items-center justify-between border-t-2 border-border pt-6">
                        <Button type="button" variant="outline" onClick={prevStep} disabled={step === 1 || isSubmitting} className={cn("border-2 border-border rounded-none uppercase font-extrabold tracking-widest", step === 1 && "invisible")}>
                            <ArrowLeft className="mr-2 size-4" />
                            Prev
                        </Button>

                        {step < totalSteps ? <Button type="button" onClick={nextStep} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-extrabold tracking-widest px-8">
                                Next Step
                                <ArrowRight className="ml-2 size-4" />
                            </Button> : <Button type="submit" disabled={isSubmitting} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-none uppercase font-extrabold tracking-widest min-w-[200px]">
                                {isSubmitting ? <>
                                        <Loader className="mr-2 size-4 animate-spin" />
                                        Transmitting...
                                    </> : <>
                                        Secure Dispatch
                                        <CircleCheck className="ml-2 size-4" />
                                    </>}
                            </Button>}
                    </div>
                </Form>
            </CardContent>
        </Card>;
}